import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Package, Tag, Calendar, Truck, Wallet, Box, BarChart3,
  Film, LogOut, Milk, Menu, X, Palette, Cloud, Sparkles, Heart, Gift, Store, Brain,
  Image as ImageIcon, ShoppingCart, CreditCard, Smartphone, Bell, Clock, Copy, Percent,
  Upload, Building2,
} from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import usePushRegistration from "../../lib/usePushRegistration";
import { personaForRole } from "../../lib/pushNotifications";
import RoleSwitcher from "../../components/RoleSwitcher";
import { LoadingState } from "../../components/EmptyState";

// `module`=null means always visible. Otherwise the tab is shown only when
// tenant.enabled_modules includes that key.
// `supervisor`=true means this tab is also shown to the restricted
// "supervisor" role (operations only — no settings/billing/catalog/branding).
// Every other tab is tenant_admin/super_admin only.
const adminTabs = [
  { to: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard", module: null, supervisor: true },
  { to: "/admin/customers", icon: Users, label: "Customers", module: null },
  { to: "/admin/discounts", icon: Percent, label: "Discounts", module: null },
  { to: "/admin/trials", icon: Clock, label: "On Trial", module: null },
  { to: "/admin/categories", icon: Tag, label: "Categories", module: null },
  { to: "/admin/brands", icon: Sparkles, label: "Brands", module: null },
  { to: "/admin/banners", icon: ImageIcon, label: "Banners", module: null },
  { to: "/admin/products", icon: Package, label: "Products", module: null },
  { to: "/admin/orders", icon: ShoppingCart, label: "Orders", module: null, supervisor: true },
  { to: "/admin/subscriptions", icon: Calendar, label: "Subscriptions", module: "subscriptions" },
  { to: "/admin/deliveries", icon: Truck, label: "Deliveries", module: "delivery", supervisor: true },
  { to: "/admin/routes", icon: Truck, label: "Routes & Riders", module: "delivery", supervisor: true },
  // Always listed (module: null) so an admin can build the apartment list
  // BEFORE switching the feature on for customers in Settings.
  { to: "/admin/apartments", icon: Building2, label: "Apartments", module: null, supervisor: true },
  { to: "/admin/bottles", icon: Box, label: "Bottles", module: "bottles" },
  { to: "/admin/reels", icon: Film, label: "Reels", module: "reels" },
  { to: "/admin/marketplace", icon: Store, label: "Marketplace", module: "multi_seller" },
  { to: "/admin/loyalty", icon: Heart, label: "Loyalty", module: "loyalty" },
  { to: "/admin/referrals", icon: Gift, label: "Referrals", module: "referral" },
  { to: "/admin/ai-insights", icon: Brain, label: "AI Insights", module: "ai_insights" },
  { to: "/admin/reports", icon: BarChart3, label: "Reports", module: "reports", supervisor: true },
  { to: "/admin/wallet", icon: Wallet, label: "Platform Wallet", module: null },
  { to: "/admin/plan", icon: CreditCard, label: "My Plan & Addons", module: null },
  { to: "/admin/settings", icon: Palette, label: "Settings & Delivery Slots", module: null },
  { to: "/admin/pwa", icon: Smartphone, label: "Android App (TWA)", module: null },
];

const superTabs = [
  { to: "/super/dashboard", icon: LayoutDashboard, label: "Dashboard", module: null },
  { to: "/super/tenants", icon: Users, label: "Tenants", module: null },
  { to: "/super/wallets", icon: Wallet, label: "Tenant Wallets", module: null },
  { to: "/super/plans", icon: Wallet, label: "Plans & Addons", module: null },
  { to: "/super/push", icon: Bell, label: "Push Notifications", module: null },
  { to: "/super/migrate", icon: Copy, label: "Migrate Tenant", module: null },
  { to: "/super/import-customers", icon: Upload, label: "Import Customers", module: null },
  { to: "/super/settings", icon: Cloud, label: "Platform Settings", module: null },
];

function trialDaysLeft(tenant) {
  if (!tenant?.trial_expires_at || tenant?.domain_status === "live") return null;
  const ms = new Date(tenant.trial_expires_at) - new Date();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

export default function AdminLayout({ superAdmin = false }) {
  const { user, tenant, logout, loading } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [billing, setBilling] = useState(null);  // { status: paid/trial/trial_expired, days_left }

  // Register this device for push under the "admin" persona. Without this the
  // back-office app held NO device token at all, so order alerts had nowhere
  // to go however correct the backend was. Super admin is excluded — those
  // alerts are tenant-scoped operational notices. No-op on the web.
  // Persona is derived from the role (personaForRole maps tenant_admin and
  // supervisor to "admin"), so it stays correct no matter which section of
  // the shared app the user last visited. Super admin is excluded — those
  // alerts are tenant-scoped.
  usePushRegistration(personaForRole(user?.role), !superAdmin && !!user?.role);

  // Fetch trial state once per session
  useEffect(() => {
    if (!user || superAdmin || user.role !== "tenant_admin") return;
    api.get("/api/admin/billing/state")
      .then(({ data }) => setBilling(data.trial))
      .catch(() => {});
  }, [user, superAdmin]);

  // Soft block: when trial expired AND user is not already on the Plan page,
  // bounce them to /admin/plan. Customer storefront keeps working.
  useEffect(() => {
    if (!billing || superAdmin) return;
    if (billing.status === "trial_expired" && !location.pathname.startsWith("/admin/plan")) {
      nav("/admin/plan", { replace: true });
    }
  }, [billing, location.pathname, superAdmin, nav]);

  const allTabs = superAdmin ? superTabs : adminTabs;
  // `effective_modules` is the authoritative list (core + plan-included + active
  // addon_subs) computed server-side in /api/auth/me. Fall back to the legacy
  // stored field if the backend hasn't been redeployed yet.
  const enabled = new Set(tenant?.effective_modules || tenant?.enabled_modules || []);
  // Bottle tracking has its own admin toggle (Settings → Operations). When the
  // tenant turns it off, the Bottles tab disappears from the sidebar even if
  // the addon/module is still in `enabled_modules`.
  const bottlesOff = tenant && tenant.bottle_tracking_enabled === false;
  const isSupervisor = !superAdmin && user?.role === "supervisor";
  const tabs = superAdmin
    ? allTabs
    : allTabs.filter((t) => {
        if (t.module && !enabled.has(t.module)) return false;
        if (t.module === "bottles" && bottlesOff) return false;
        if (isSupervisor && !t.supervisor) return false;
        return true;
      });
  const [open, setOpen] = useState(false);
  const daysLeft = !superAdmin
    ? (billing ? (billing.status === "paid" ? null : billing.days_left) : trialDaysLeft(tenant))
    : null;

  if (loading) return <LoadingState fullHeight className="min-h-screen" />;
  if (!user) { nav(superAdmin ? "/super-admin/login" : "/admin/login"); return null; }
  if (superAdmin && user.role !== "super_admin") { nav("/"); return null; }
  if (!superAdmin && !["tenant_admin", "supervisor"].includes(user.role)) { nav("/"); return null; }

  const SidebarContent = () => (
    <>
      <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <div className="w-10 h-10 rounded-2xl bg-brand-sky flex items-center justify-center">
            <Milk className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-extrabold tracking-tight" data-testid="sidebar-brand">
              {superAdmin ? "DairyFresh" : (tenant?.name || "Admin")}
            </div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">
              {superAdmin ? "Super Admin" : "Operator Console"}
            </div>
          </div>
        </Link>
        <button onClick={() => setOpen(false)} className="lg:hidden w-9 h-9 rounded-2xl bg-brand-cream flex items-center justify-center" data-testid="sidebar-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>
      {daysLeft !== null && (
        <div className={`mx-3 mt-3 rounded-2xl px-3 py-2.5 text-xs font-bold flex items-center gap-2 ${
          daysLeft <= 1 ? "bg-brand-clay/10 text-brand-clay" : "bg-brand-sand/15 text-brand-sand"
        }`} data-testid="trial-banner">
          <Sparkles className="w-3.5 h-3.5"/>
          <span>{daysLeft > 0 ? `Trial: ${daysLeft} day${daysLeft === 1 ? "" : "s"} left` : "Trial expired"}</span>
          <Link to="/admin/plan" className="ml-auto underline">Upgrade</Link>
        </div>
      )}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {tabs.map((t) => (
          <NavLink key={t.to} to={t.to} onClick={() => setOpen(false)}
                   className={({ isActive }) =>
                     `flex items-center gap-3 px-3 py-2.5 rounded-2xl text-sm font-bold transition-colors ${
                       isActive ? "bg-brand-sky/10 text-brand-sky" : "text-neutral-700 hover:bg-neutral-100"
                     }`}
                   data-testid={`nav-${t.label.toLowerCase().replace(/[& ]/g, '-')}`}>
            <t.icon className="w-4.5 h-4.5" /> {t.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-neutral-200">
        <div className="px-3 py-2 mb-2">
          <div className="text-xs font-bold truncate">{user.name}</div>
          <div className="text-[10px] text-neutral-500 truncate">{user.phone || user.email}</div>
        </div>
        <RoleSwitcher compact />
        <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-2xl text-sm font-bold text-brand-clay hover:bg-brand-clay/10" data-testid="logout-btn">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-brand-cream" data-testid="admin-layout">
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-neutral-200 pt-safe" data-testid="mobile-header">
        <div className="flex items-center justify-between px-4 h-14">
          <button onClick={() => setOpen(true)} className="w-10 h-10 rounded-2xl bg-brand-cream flex items-center justify-center" data-testid="mobile-menu-btn" aria-label="Open menu">
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-brand-sky flex items-center justify-center">
              <Milk className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-extrabold tracking-tight">
              {superAdmin ? "DairyFresh" : (tenant?.name || "Admin")}
            </span>
          </Link>
          <div className="w-10" />
        </div>
      </header>

      <aside className="hidden lg:flex w-64 bg-white border-r border-neutral-200 fixed h-screen flex-col z-40" data-testid="admin-sidebar">
        <SidebarContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <button aria-label="Close menu" onClick={() => setOpen(false)} className="absolute inset-0 bg-black/40 backdrop-blur-sm" data-testid="drawer-backdrop"/>
          <aside className="relative w-72 max-w-[85vw] bg-white h-full flex flex-col animate-fade-in-up pt-safe">
            <SidebarContent />
          </aside>
        </div>
      )}

      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8" data-testid="admin-main">
        <Outlet />
      </main>
    </div>
  );
}
